import {
  BaseInspectionSelectionsRouteService,
  InspectionSelectionsInitialData,
} from "@/model/BaseInspectionSelectionsRouteService";
import requestService from "@/service/requestService";

/*
 * WHY: routeName ごとに gensha/legal56/jizen の root key が変わるため、
 * page から apiPath 依存の配列読み取りをなくす。
 */
function extractCategories(response: any, apiPath: string): any[] {
  return response && Array.isArray(response[apiPath])
    ? response[apiPath]
    : [];
}

/*
 * WHY: 初期表示 data、カテゴリ一覧、遅延読込対象、進捗 state は同じ API 応答から作るため、
 * page では routeService の結果を適用するだけにする。
 */
function buildInitialData(
  response: any,
  apiPath: string
): InspectionSelectionsInitialData {
  const categoryItems = extractCategories(response, apiPath);
  return {
    data: {
      [apiPath]: categoryItems
    },
    categoryItems,
    lazyLoadCategoryIds: getLazyLoadCategoryIds(categoryItems, apiPath),
    progressState: {
      stateId: response ? response.sknWorkProgressStateId : null,
      stateNm: response ? response.sknWorkProgressStateNm : null
    }
  };
}

/*
 * WHY: 保存 payload の root key は route ごとに違うため、page に data[apiPath] 生成を置かない。
 */
function buildInspectSavePayload(apiPath: string, inspectDescriptions: any): any {
  const data: any = {};
  data[apiPath] = [
    {
      subItems: [
        {
          inspectDescriptions: Array.isArray(inspectDescriptions)
            ? inspectDescriptions
            : []
        }
      ]
    }
  ];
  return data;
}

/*
 * WHY: jizen の osusume カテゴリだけ遅延読込対象から外す例外があるため、
 * routeService が lazy load 対象を決める。
 */
function getLazyLoadCategoryIds(categoryItems: any[], apiPath: string): any[] {
  return categoryItems
    .filter((e: any, i: number) => {
      if (i == 0) {
        return false;
      }
      if (apiPath == "jizen" && e.osusume) {
        return false;
      }
      if (e.subItems && e.subItems.length > 0) {
        return false;
      }
      return true;
    })
    .map((e: any) => e.sknInspectCategoryId);
}

/*
 * WHY: カテゴリ追加読込 API の応答から subItems を戻す処理も apiPath 依存なので、
 * page から root key とカテゴリ検索を隠す。
 */
function extractCategorySubItems(
  response: any,
  apiPath: string,
  sknInspectCategoryId: any
): any[] {
  const category = extractCategories(response, apiPath).find((item: any) => {
    return item.sknInspectCategoryId == sknInspectCategoryId;
  });
  return category && Array.isArray(category.subItems)
    ? category.subItems
    : [];
}

export function createBaseInspectionSelectionsRouteService(params: {
  routeName: string;
  inspectType: number;
  headerTopic: string;
  apiPath: string;
  reqService: requestService;
  getInspect: (
    uketsukeExtendsId: number,
    sknSeibiShubetsuKbn?: string
  ) => Promise<any>;
  getInspectByCategoryId: (
    uketsukeExtendsId: number,
    sknInspectCategoryId: number,
    sknSeibiShubetsuKbn?: string
  ) => Promise<any>;
  saveInspect: (data: any) => Promise<any>;
  getSknSeibiShubetsuKbn?: (sknUketsukeExtends: any) => string;
  getRegisterStatus?: (categories: any[], inspectService: any) => string;
  shouldUpdateUketsukeExtendsOnRegister?: () => boolean;
  shouldAutoResetJizenRecommend?: () => boolean;
  shouldAutoSetJizenRecommend?: (notManual: boolean) => boolean;
  shouldClearSocketAfterInspectSave?: (sendOneSocket: boolean) => boolean;
  shouldRemoveUnlinkedDisabledSaveSeibi?: (seibi: any) => boolean;
  recommendService?: any;
}): BaseInspectionSelectionsRouteService {
  return {
    routeName: params.routeName,
    inspectType: params.inspectType,
    headerTopic: params.headerTopic,
    apiPath: params.apiPath,
    recommendService: params.recommendService,
    getInspect: params.getInspect,
    getInspectByCategoryId: params.getInspectByCategoryId,
    saveInspect: params.saveInspect,
    /*
     * WHY: route ごとに異なる API root key を service が持ち、保存処理の payload 形を固定する。
     */
    buildInspectSavePayload(inspectDescriptions: any): any {
      return buildInspectSavePayload(params.apiPath, inspectDescriptions);
    },
    /*
     * WHY: API 応答に対象 route の body があるかを service で判定し、page から pathName 判定を消す。
     */
    hasRouteBody(response: any): boolean {
      return !!(
        response &&
        response.body &&
        Array.isArray(response.body[params.apiPath])
      );
    },
    /*
     * WHY: 初期表示に必要な route 固有 state をまとめて作り、page は代入に集中させる。
     */
    buildInitialData(response: any): InspectionSelectionsInitialData {
      return buildInitialData(response, params.apiPath);
    },
    /*
     * WHY: API 応答からカテゴリ配列を読む root key を routeService に閉じ込める。
     */
    extractCategories(response: any): any[] {
      return extractCategories(response, params.apiPath);
    },
    /*
     * WHY: 現在保持中 data の root key も route ごとに違うため、page から data.gensha 等を消す。
     */
    extractCurrentCategories(data: any): any[] {
      return extractCategories(data, params.apiPath);
    },
    /*
     * WHY: activeCategory から現在カテゴリを取る処理を共通化し、route ごとの root key を隠す。
     */
    getCurrentCategory(data: any, activeCategory: number): any {
      const categories = extractCategories(data, params.apiPath);
      return categories[activeCategory] || null;
    },
    /*
     * WHY: カテゴリ件数取得を apiPath 非依存の問い合わせにして、template/control から root key を隠す。
     */
    getCurrentCategoryCount(data: any): number {
      return extractCategories(data, params.apiPath).length;
    },
    /*
     * WHY: route ごとのカテゴリ有無判定を page に分散させない。
     */
    hasCurrentCategories(data: any): boolean {
      return extractCategories(data, params.apiPath).length > 0;
    },
    /*
     * WHY: 保存や追加読込後のカテゴリ差し替え先 root key を service に固定する。
     */
    replaceCurrentCategories(data: any, categories: any[]): any {
      data[params.apiPath] = categories;
      return data;
    },
    /*
     * WHY: 登録可否や表示 class で使う未完了判定をカテゴリ取得と同じ境界に置く。
     */
    isCurrentCategoryIncomplete(
      data: any,
      activeCategory: number,
      incompleteStatus: number
    ): boolean {
      const category = this.getCurrentCategory(data, activeCategory);
      return !!category && category.status === incompleteStatus;
    },
    /*
     * WHY: 遅延読込した subItems の適用先カテゴリ検索を service に寄せる。
     */
    applyCategorySubItems(
      data: any,
      sknInspectCategoryId: any,
      subItems: any[]
    ): any[] {
      const categories = extractCategories(data, params.apiPath);
      categories.forEach((category: any) => {
        if (category.sknInspectCategoryId == sknInspectCategoryId) {
          category.subItems = subItems;
        }
      });
      return categories;
    },
    /*
     * WHY: 保存応答から inspectDescriptions を読む nested path を page から隠す。
     */
    extractSavedInspectDescriptions(data: any): any[] {
      const categories = extractCategories(data, params.apiPath);
      return categories[0] &&
        categories[0].subItems &&
        categories[0].subItems[0] &&
        Array.isArray(categories[0].subItems[0].inspectDescriptions)
        ? categories[0].subItems[0].inspectDescriptions
        : [];
    },
    /*
     * WHY: jizen 保存応答に含まれる先頭 recommend の取り出し位置を service に固定する。
     */
    extractFirstSavedRecommend(data: any): any {
      const categories = extractCategories(data, params.apiPath);
      return categories[0] &&
        categories[0].recommend &&
        categories[0].recommend[0]
        ? categories[0].recommend[0]
        : null;
    },
    /*
     * WHY: jizen の先頭カテゴリ配下 recommend 取得は gensha/legal56 と形が違うため、service が読む。
     */
    extractFirstCategoryRecommendCategories(data: any): any[] {
      const categories = extractCategories(data, params.apiPath);
      return categories[0] && Array.isArray(categories[0].recommend)
        ? categories[0].recommend
        : [];
    },
    /*
     * WHY: おすすめ専用 data shape かどうかの判定を page から分離する。
     */
    hasRecommendCategories(data: any): boolean {
      return !!(data && Array.isArray(data.recommend));
    },
    /*
     * WHY: おすすめカテゴリ配列の root は通常点検と異なるため、service 経由で取得する。
     */
    extractRecommendCategories(data: any): any[] {
      return data && Array.isArray(data.recommend) ? data.recommend : [];
    },
    /*
     * WHY: jizen は activeCategory/activeSubCategory の二段階でおすすめカテゴリを決めるため、
     * page に jizen 専用 ID 取得ロジックを置かない。
     */
    getJizenRecommendCategoryId(
      data: any,
      activeCategory: number,
      activeSubCategory: number,
      fallback?: any
    ): any {
      const category = this.getActiveRecommendCategory(
        data,
        activeCategory,
        activeSubCategory
      );
      if (category && category.sknRecommendInspectCategoryId != null) {
        return category.sknRecommendInspectCategoryId;
      }
      return fallback != null ? fallback : null;
    },
    /*
     * WHY: 通常おすすめ保存で使うカテゴリ ID 取得を service に集約し、fallback の扱いも固定する。
     */
    getRecommendCategoryId(
      data: any,
      activeCategory: number,
      fallback?: any
    ): any {
      const recommendCategories = this.extractRecommendCategories(data);
      const category = recommendCategories[activeCategory] || null;
      if (category && category.sknRecommendInspectCategoryId != null) {
        return category.sknRecommendInspectCategoryId;
      }
      return fallback != null ? fallback : null;
    },
    /*
     * WHY: カテゴリ別 API 応答から subItems を抜く nested path を page から隠す。
     */
    extractCategorySubItems(response: any, sknInspectCategoryId: any): any[] {
      return extractCategorySubItems(
        response,
        params.apiPath,
        sknInspectCategoryId
      );
    },
    /*
     * WHY: jizen だけ整備種別区分の算出方法が違うため、route service が差し替える。
     */
    getSknSeibiShubetsuKbn(sknUketsukeExtends: any): string {
      return params.getSknSeibiShubetsuKbn
        ? params.getSknSeibiShubetsuKbn(sknUketsukeExtends)
        : "";
    },
    /*
     * WHY: 登録 status の算出は route ごとに違うため、page は routeService に問い合わせる。
     */
    getRegisterStatus(categories: any[], inspectService: any): string {
      return params.getRegisterStatus
        ? params.getRegisterStatus(categories, inspectService)
        : inspectService.getRegisterStatus(categories);
    },
    /*
     * WHY: 登録時に uketsukeExtends 更新が必要かは route 固有仕様なので、明示判定にする。
     */
    shouldUpdateUketsukeExtendsOnRegister(): boolean {
      return params.shouldUpdateUketsukeExtendsOnRegister
        ? params.shouldUpdateUketsukeExtendsOnRegister()
        : false;
    },
    /*
     * WHY: jizen のおすすめ reset は jizen 専用仕様のため、default 判定を page に置かない。
     */
    shouldAutoResetJizenRecommend(): boolean {
      return params.shouldAutoResetJizenRecommend
        ? params.shouldAutoResetJizenRecommend()
        : false;
    },
    /*
     * WHY: jizen のおすすめ自動選択は notManual に依存するため、route service 側で判断する。
     */
    shouldAutoSetJizenRecommend(notManual: boolean): boolean {
      return params.shouldAutoSetJizenRecommend
        ? params.shouldAutoSetJizenRecommend(notManual)
        : false;
    },
    /*
     * WHY: 保存後 socket flag のクリア有無は route 固有のため、page の if 分岐を減らす。
     */
    shouldClearSocketAfterInspectSave(sendOneSocket: boolean): boolean {
      return params.shouldClearSocketAfterInspectSave
        ? params.shouldClearSocketAfterInspectSave(sendOneSocket)
        : sendOneSocket;
    },
    /*
     * WHY: 紐づかない disabledSave 整備を除外する条件は route 固有なので、service に閉じる。
     */
    shouldRemoveUnlinkedDisabledSaveSeibi(seibi: any): boolean {
      return params.shouldRemoveUnlinkedDisabledSaveSeibi
        ? params.shouldRemoveUnlinkedDisabledSaveSeibi(seibi)
        : false;
    },
    /*
     * WHY: jizen の active recommend はカテゴリとサブカテゴリの二段階構造なので、取得方法を固定する。
     */
    getActiveRecommendCategory(
      data: any,
      activeCategory: number,
      activeSubCategory: number
    ): any {
      const categories = extractCategories(data, params.apiPath);
      return categories[activeCategory] &&
        categories[activeCategory].recommend
        ? categories[activeCategory].recommend[activeSubCategory]
        : null;
    }
  };
}
